import React from 'react';
import { MenuIcon } from '@components-library/Icons/iconsComponents';
import { EnumUserRoute } from '@components-member/userRoutes/enums';
import { keyMap } from '@locales/lokalise/keyMap';
import { IOnboardingConfiguration, IUser } from 'global.interfaces';
import i18next from 'i18next';
import { FeaturesStore } from 'store/mobx/features.store';
import type { TradingAccountsStore } from 'store/mobx/tradingAccounts.store';
import { MenuItemType, SubmenuItemType } from '.';

type MenuConfigData = {
  tradingAccountsStore: TradingAccountsStore;
  featuresStore: FeaturesStore;
};

export const getCompleteMenuConfig = (configData: MenuConfigData): MenuItemType[] => {
  const { tradingAccountsStore, featuresStore } = configData;

  const {
    TradingContests: TradingContestsFeature,
    SocialTrading: SocialTradingFeature,
    Bonuses: BonusesFeature,
    LoyaltyPrograms: LoyaltyProgramsFeature,
  } = featuresStore.featureFlags;

  return [
    {
      name: keyMap.settingsProfileFormTitle,
      type: 'menuItem',
      path: '/member/profile',
      icon: <MenuIcon icon="profile" />,
    },
    {
      name: keyMap.menuAccounts,
      type: 'menuItem',
      path: '/member',
      icon: <MenuIcon icon="accounts" />,
      exact: true,
      additionalMatchPaths: ['/member/tradingaccounts'],
    },
    {
      name: keyMap.menuFunding,
      type: 'menuItem',
      path: '/member/psp',
      icon: <MenuIcon icon="funding" />,
    },
    {
      name: keyMap.menuTradingPlatforms,
      type: 'menuItem',
      path: EnumUserRoute.tradingPlatforms,
      icon: <MenuIcon icon="tradingPlatforms" />,
    },
    {
      name: keyMap.menuTradingTools,
      type: 'menuItem',
      path: EnumUserRoute.tradingTools,
      icon: <MenuIcon icon="tradingTools" />,
      isHidden: (data) => data?.showTradingToolTab === false,
    },
    {
      name: keyMap.menuSocialTrading,
      type: 'subMenuItem',
      children: [
        {
          name: keyMap.menuOpenMastersAccount,
          path: '/member?openaccount=master&source=sidebar&platform-type=none',
          onClick: () => {
            /** path above includes all neccessary params, no need to specify any */
            tradingAccountsStore.configureCreateTradingAccountModal({
              queryParams: {},
            });
          },
        },
        {
          name: keyMap.menuOpenInvestorsAccount,
          path: '/member?openaccount=follower&source=sidebar&platform-type=none',
          onClick: () => {
            /** path above includes all neccessary params, no need to specify any */
            tradingAccountsStore.configureCreateTradingAccountModal({ queryParams: {} });
          },
        },
        { name: keyMap.menuRatings, path: EnumUserRoute.mastersRatingPage },
        { name: keyMap.menuStatistics, path: '/member/socialtrading/mystatistics' },
        { name: keyMap.menuHistory, path: '/member/socialtrading/operationhistory' },
      ],
      icon: <MenuIcon icon="socialTrading" />,
      isHidden: (data) => data?.showCopyTradingTab === false || !SocialTradingFeature,
    },
    {
      name: keyMap.menuNotificationsAndMessages,
      type: 'menuItem',
      path: EnumUserRoute.notificationsPage,
      icon: <MenuIcon icon="notifications" />,
    },
    {
      name: keyMap.menuTradingContests,
      type: 'menuItem',
      path: EnumUserRoute.tradingContestPage,
      icon: <MenuIcon icon="tradingContest" />,
      isHidden: (data) => data?.showTradingContestsTab === false || !TradingContestsFeature,
    },
    {
      name: keyMap.menuLoyaltyProgram,
      type: 'menuItem',
      path: EnumUserRoute.loyaltyProgramPage,
      icon: <MenuIcon icon="loyaltyProgram" />,
      isHidden: (data) => data?.showLoyaltyProgramsTab === false || !LoyaltyProgramsFeature,
    },
    {
      name: keyMap.menuHelpCenter,
      type: 'subMenuItem',
      children: [
        {
          name: keyMap.menuTickets,
          path: '/member/helpcenter/usertickets',
        },
        {
          name: keyMap.menuFaq,
          path: '/member/helpcenter/faq',
          isHidden: (data) => data?.showFaqTab === false,
        },
        {
          name: keyMap.menuEducationPortal,
          path: EnumUserRoute.educationPortal,
          isHidden: (data) => data?.showEducationPortalTab === false,
        },
        {
          name: keyMap.menuLegal,
          path: '/member/helpcenter/documents',
        },
      ],
      icon: <MenuIcon icon="helpCenter" />,
    },
    {
      name: keyMap.menuPartnership,
      type: 'menuItem',
      path: EnumUserRoute.partnershipPage,
      icon: <MenuIcon icon="partnership" />,
      isHidden: (data) => data?.canAddToTree === false,
    },
    {
      name: keyMap.menuSales,
      type: 'menuItem',
      path: '/member/sales',
      icon: <MenuIcon icon="sales" />,
      isHidden: (data) => data?.isSales === false,
    },
    {
      name: keyMap.bonusesTitle,
      type: 'menuItem',
      path: '/member/bonuses',
      icon: <MenuIcon icon="bonuses" />,
      isHidden: (data) => data?.showBonusesTab === false || !BonusesFeature,
    },
    {
      name: keyMap.menuSettings,
      type: 'menuItem',
      path: '/member/settings',
      icon: <MenuIcon icon="settings" />,
    },
  ];
};

const filterHiddenMenuItems = (
  user: IUser,
  userTabsSettings: IOnboardingConfiguration,
  items: Array<MenuItemType | SubmenuItemType>,
): Array<MenuItemType | SubmenuItemType> => {
  return items.filter((m) => !(m.isHidden && (m.isHidden(user) || m.isHidden(userTabsSettings))));
};

const setNameForSocialTradingTab = (menuItems: MenuItemType[], userTabsSettings: IOnboardingConfiguration): void => {
  const socialTrading = menuItems.find((x) => x.name == keyMap.menuSocialTrading);
  const name = i18next.t(keyMap.menuSocialTrading, { companyName: userTabsSettings.companyNameForCopyTradingTab });

  if (socialTrading) {
    socialTrading.name = name;
  }
};

export const getMenuConfig = (
  user: IUser,
  userTabsSettings: IOnboardingConfiguration,
  configData: MenuConfigData,
): Array<MenuItemType> => {
  const menuItems = filterHiddenMenuItems(
    user,
    userTabsSettings,
    getCompleteMenuConfig(configData),
  ) as Array<MenuItemType>;

  menuItems.forEach((item) => {
    if (item.children) {
      item.children = filterHiddenMenuItems(user, userTabsSettings, item.children);
    }
  });

  setNameForSocialTradingTab(menuItems, userTabsSettings);

  return menuItems;
};
